import React, { useState, useEffect, useRef } from "react";
import "./ClientsHome.css";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaGlobe,
  FaUsers,
  FaStar,
  FaDesktop,
  FaExternalLinkAlt,
  FaPaintRoller,
  FaRegClock,
  FaHeadset
} from "react-icons/fa";
import { projects } from "../../data/siteData";
import heroImg from "../../Asset/hero-portfolio.png";



/* =========================================
   SINGLE PROJECT CARD COMPONENT
========================================= */
function ProjectCard({ project, index }) {
  const [cur, setCur] = useState(0);
  const imgs = project?.images || [];
  const multi = imgs.length > 1;
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  /* Auto-slide images */
  useEffect(() => {
    if (!multi) return;
    const id = setInterval(() => setCur((p) => (p === imgs.length - 1 ? 0 : p + 1)), 3500);
    return () => clearInterval(id);
  }, [multi, imgs.length]);

  /* Scroll Reveal Animation */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const go = (dir, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCur((p) => (dir === 1 ? (p === imgs.length - 1 ? 0 : p + 1) : p === 0 ? imgs.length - 1 : p - 1));
  };

  const isEven = index % 2 !== 0; 
  const techStack = project.features || ["React.js", "Django", "MySQL"];
  const visibleTech = techStack.slice(0, 3);
  const remainingTech = techStack.length > 3 ? techStack.length - 3 : 0;

  return (
    <div ref={cardRef} className={`ag-card ${visible ? "ag-card--visible" : ""}`}>

      {/* --- IMAGE SECTION --- */}
      <div className="ag-card__visual">
        {index === 0 && <span className="ag-featured-tag">Featured</span>}

        <div className="ag-card__slider">
          {imgs.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${project.title} screenshot ${i + 1}`}
              className={`ag-card__img ${i === cur ? "ag-card__img--active" : ""}`}
            />
          ))}
        </div>

        {multi && (
          <div className="ag-slider-controls">
            <button className="ag-arrow" onClick={(e) => go(-1, e)}><FaChevronLeft /></button>
            <div className="ag-dots">
              {imgs.map((_, i) => (
                <span key={i} className={`ag-dot ${i === cur ? "ag-dot--active" : ""}`} onClick={(e) => { e.preventDefault(); setCur(i); }} />
              ))}
            </div>
            <button className="ag-arrow" onClick={(e) => go(1, e)}><FaChevronRight /></button>
          </div>
        )}
      </div>

      {/* --- CONTENT SECTION --- */}
      <div className="ag-card__content">
        <div className="ag-card__header">
          <div className="ag-card__category">
            <div className="ag-cat-icon"><FaDesktop /></div>
            <span>WEBSITE & MOBILE APP</span>
          </div>
          <div className="ag-card__number">
            {isEven ? "" : "— "}
            {String(index + 1).padStart(2, '0')}
            {isEven ? " —" : ""}
          </div>
        </div>

        <h3 className="ag-card__title">{project.title}</h3>

        <p className="ag-card__desc">
          A modern digital platform designed to help users connect, engage, and grow their community with seamless features and responsive design.
        </p>

        <div className="ag-card__tech">
          {visibleTech.map((tech, i) => (
            <span key={i} className="ag-tech-pill">{tech}</span>
          ))}
          {remainingTech > 0 && (
            <span className="ag-tech-pill ag-tech-pill--count">+{remainingTech}</span>
          )}
        </div>

        <div className="ag-card__actions">
          <a href={project.link} target="_blank" rel="noopener noreferrer" className="ag-btn ag-btn--dark">
            View Project <FaArrowRight className="btn-arrow" />
          </a>
        </div>
      </div>
    </div>
  );
}


/* =========================================
   MAIN PORTFOLIO PAGE
========================================= */
export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredProjects = projects.filter(p => {
    const matchSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  return (
    <div className="ag-portfolio" id="portfolio">

      {/* Background Decor */}
      <div className="ag-bg-glow ag-bg-glow--top"></div>
      <div className="ag-bg-pattern"></div>

      {/* ================= HERO SECTION ================= */}
      <div className="ag-hero">
        <div className="ag-hero__text">
          <p className="ag-eyebrow">— OUR PORTFOLIO</p>
          <h1 className="ag-heading">
            Ideas to Digital<br />
            <span className="ag-heading--highlight">Real Solutions</span>
          </h1>
          <p className="ag-subtext">
            We've helped businesses, organizations and startups turn their ideas into powerful websites and mobile applications. Explore some of our recent work and see how we create custom solutions for real-world needs.
          </p>

          <div className="ag-stats">
            <div className="ag-stat">
              <div className="ag-stat__icon-box"><FaGlobe /></div>
              <div>
                <strong>100+</strong>
                <span>Websites & Mobile Apps</span>
              </div>
            </div>
            <div className="ag-stat">
              <div className="ag-stat__icon-box"><FaUsers /></div>
              <div>
                <strong>100+</strong>
                <span>Happy Clients</span>
              </div>
            </div>
            <div className="ag-stat">
              <div className="ag-stat__icon-box"><FaStar /></div>
              <div>
                <strong>50+</strong>
                <span>Industries</span>
              </div>
            </div>
          </div>
        </div>

        {/* --- ENHANCED HERO IMAGE AREA --- */}
        <div className="ag-hero__image-area">
           {/* Floating Badges */}
           <div className="ag-hero-badge ag-hero-badge--top">
             <FaStar className="badge-icon" /> Top Rated Design
           </div>
           <div className="ag-hero-badge ag-hero-badge--bottom">
             <FaDesktop className="badge-icon" /> Websites & Apps
           </div>

           <img src={heroImg} alt="Hero Portfolio" className="ag-hero__img" />
        </div>
      </div>

      {/* ================= CONTROLS (FILTER & SEARCH) ================= */}
      <div className="ag-controls">
        
        <div className="ag-search">
          <FaSearch className="ag-search__icon" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* ================= PROJECT LIST ================= */}
      <div className="ag-project-list">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))
        ) : (
          <p className="ag-no-results">No projects found matching your search.</p>
        )}
      </div>

     

    </div>
  );
}